import { SplitFactory } from '@splitsoftware/splitio'
import { LocalOnlyFeatures } from '@/plugins/split/LocalOnlyFeatures'
import { applicationSplits, platformSplits } from '@/plugins/split/ActiveSplits'

const APPLICATION_KEY = 'segments'

/**
 * Split Manager is a stateful class to hold SplitIO clients
 * and all associated treatments
 */
export class SplitManager {
  _splitApiKey = process.env.VUE_APP_SPLIT_API_KEY

  _applicationSplitClient = null
  _platformSplitClient = null

  _treatments = {}

  /**
   * Checks if a given split is enabled by name
   * @param {string} splitName
   * @return {boolean}
   */
  isSplitEnabled (splitName) {
    return this._treatments[splitName]?.enabled || false
  }

  /**
   * Init method to create SplitIO clients and fetch
   * related treatments
   * @param {string} platformHash
   * @param {string} svpUtid
   * @return {Promise<void>}
   */
  async init (platformHash, svpUtid) {
    await this.createPlatformClient(platformHash, svpUtid)
    await this.createApplicationClient()
  }

  /**
   * Creat method for Platform SplitIO client
   * @param {string} platformHash
   * @param {string} svpUtid
   * @return {Promise<void>}
   */
  async createPlatformClient (platformHash, svpUtid) {
    if (this._platformSplitClient !== null) {
      return
    }
    this._platformSplitClient = await this.onSdkReady(this.initSplitClient(platformHash))
    this.applyPlatformTreatments(svpUtid)
  }

  /**
   * @private
   * Fetches all treatments for Platform SplitIO client
   * @param {string} svpUtid
   */
  applyPlatformTreatments (svpUtid) {
    platformSplits.forEach(split => {
      const treatment = this._platformSplitClient.getTreatment(split.name, { utid: svpUtid })
      this._treatments[split.name] = {
        treatment: treatment,
        enabled: treatment === split.enabledValue
      }
    })
  }

  /**
   * Creat method for Application SplitIO client
   * @return {Promise<void>}
   */
  async createApplicationClient () {
    if (this._applicationSplitClient !== null) {
      return
    }
    this._applicationSplitClient = await this.onSdkReady(this.initSplitClient(APPLICATION_KEY))
    this.applyApplicationTreatments()
  }

  /**
   * @private
   * Fetches all treatments for Application SplitIO client
   */
  applyApplicationTreatments () {
    applicationSplits.forEach(split => {
      const treatment = this._applicationSplitClient.getTreatment(split.name)
      this._treatments[split.name] = {
        treatment: treatment,
        enabled: treatment === split.enabledValue
      }
    })
  }

  /**
   * @private
   * Helper function to catch split client ready
   * or split client timeout
   * @param splitClient
   * @return {Promise<SplitIO.IClient|Error>}
   */
  async onSdkReady (splitClient) {
    return await new Promise((resolve, reject) => {
      splitClient.on(splitClient.Event.SDK_READY, () => {
        resolve(splitClient)
      }).on(splitClient.Event.SDK_READY_TIMED_OUT, () => {
        reject(new Error('Split Client timed out'))
      })
    })
  }

  /**
   * @private
   * Helper function to build SplitFactory SplitIO client
   * @param {string} key
   * @return {SplitIO.IClient}
   */
  initSplitClient (key) {
    const factoryConfig = {
      core: {
        authorizationKey: this._splitApiKey,
        key
      },
      startup: {
        readyTimeout: 1.5
      }
    }

    if (this._splitApiKey === 'localhost') {
      factoryConfig.features = LocalOnlyFeatures
    }

    return SplitFactory(factoryConfig).client()
  }
}

/**
 * @type SplitManager
 */
export const SplitClient = new SplitManager()
